import { CalendarIcon, Plus, Trash2, X } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  type FirmRoleAssignmentWithWeight,
  type TeamMemberPayload,
  useSetTeamDistributionMutation,
} from '@/services/api/firmsService';
import { useGetStaffUsersQuery } from '@/services/api/userService';
import { cn } from '@/lib/utils';
import { getUserDisplayName } from '@/utils/user';

const MEMBER_COLORS = [
  { bar: 'bg-violet-500' },
  { bar: 'bg-sky-500' },
  { bar: 'bg-emerald-500' },
  { bar: 'bg-amber-500' },
  { bar: 'bg-rose-500' },
  { bar: 'bg-indigo-500' },
];

const getMemberColor = (index: number) => MEMBER_COLORS[index % MEMBER_COLORS.length];

interface EditMember {
  assignee_id: string;
  display_name: string;
  weight: number;
  has_availability: boolean;
  available_from: string;
  available_until: string;
}

type Props = {
  firmId: string;
  role: string;
  roleLabel: string;
  currentAssignments: FirmRoleAssignmentWithWeight[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
  /** Optional override: when provided, called instead of the default per-firm mutation. */
  onSaveMembers?: (members: TeamMemberPayload[]) => Promise<void>;
};

function initMembersFromAssignments(
  assignments: FirmRoleAssignmentWithWeight[],
): EditMember[] {
  return assignments.map((a) => ({
    assignee_id: String(a.assignee_id),
    display_name: a.assignee.display_name,
    weight: Math.round((a.config?.weight ?? 1) * 100),
    has_availability: !!(a.config?.available_from || a.config?.available_until),
    available_from: a.config?.available_from ?? '',
    available_until: a.config?.available_until ?? '',
  }));
}

export const EditTeamDialog: React.FC<Props> = ({
  firmId,
  role,
  roleLabel,
  currentAssignments,
  open,
  onOpenChange,
  onSaveMembers,
  onSaved,
}) => {
  const [members, setMembers] = useState<EditMember[]>([]);
  const [addingUserId, setAddingUserId] = useState('');

  const [setTeamDistribution, { isLoading }] = useSetTeamDistributionMutation();
  const { data: staffUsers = [] } = useGetStaffUsersQuery();

  useEffect(() => {
    if (open) {
      setMembers(initMembersFromAssignments(currentAssignments));
      setAddingUserId('');
    }
  }, [open, currentAssignments]);

  const availableToAdd = useMemo(() => {
    const existingIds = new Set(members.map((m) => m.assignee_id));
    return staffUsers.filter((u) => !existingIds.has(u.id));
  }, [staffUsers, members]);

  const totalWeight = members.reduce((sum, m) => sum + Math.max(0, m.weight), 0);
  const totalIsValid = totalWeight === 100;

  const previewSegments = members
    .map((m, i) => ({
      label: m.display_name,
      pct: Math.max(0, m.weight),
      color: getMemberColor(i).bar,
    }))
    .filter((s) => s.pct > 0);

  const updateMember = (index: number, patch: Partial<EditMember>) => {
    setMembers((prev) => prev.map((m, i) => (i === index ? { ...m, ...patch } : m)));
  };

  const removeMember = (index: number) => {
    setMembers((prev) => prev.filter((_, i) => i !== index));
  };

  const addMember = () => {
    if (!addingUserId) return;
    const user = staffUsers.find((u) => u.id === addingUserId);
    if (!user) return;
    setMembers((prev) => [
      ...prev,
      {
        assignee_id: user.id,
        display_name: getUserDisplayName(user),
        weight: 0,
        has_availability: false,
        available_from: '',
        available_until: '',
      },
    ]);
    setAddingUserId('');
  };

  const handleSave = async () => {
    if (members.length === 0) {
      toast.error('Team must have at least one member');
      return;
    }
    if (members.filter((m) => m.weight > 0).length === 0) {
      toast.error('At least one member must have a percentage greater than 0');
      return;
    }
    if (totalWeight !== 100) {
      toast.error(
        totalWeight < 100
          ? `Percentages only add up to ${totalWeight}% — must total 100%`
          : `Percentages add up to ${totalWeight}% — must total 100%`,
      );
      return;
    }

    const payload: TeamMemberPayload[] = members.map((m) => ({
      assignee_id: m.assignee_id,
      weight: m.weight / 100,
      available_from: m.has_availability && m.available_from ? m.available_from : null,
      available_until: m.has_availability && m.available_until ? m.available_until : null,
    }));

    try {
      if (onSaveMembers) {
        await onSaveMembers(payload);
      } else {
        await setTeamDistribution({ firmPk: firmId, role, members: payload }).unwrap();
      }
      toast.success(`${roleLabel} distribution saved`);
      onSaved();
      onOpenChange(false);
    } catch (e: unknown) {
      const err = e as { data?: { detail?: string; error?: string } };
      toast.error(err?.data?.error ?? err?.data?.detail ?? 'Failed to save distribution');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit {roleLabel} Distribution</DialogTitle>
        </DialogHeader>

        {/* Weight preview bar */}
        <div className="space-y-2">
          <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide">
            Distribution Preview
          </p>
          <div className="flex h-3 w-full rounded-full overflow-hidden bg-zinc-100">
            {previewSegments.length > 0 ? (
              previewSegments.map((seg, i) => (
                <div
                  key={i}
                  className={cn('transition-all duration-300', seg.color)}
                  style={{ width: `${seg.pct}%` }}
                  title={`${seg.label}: ${seg.pct.toFixed(0)}%`}
                />
              ))
            ) : (
              <div className="w-full h-full bg-zinc-200 rounded-full" />
            )}
          </div>
          <div className="flex items-center justify-between">
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {previewSegments.map((seg, i) => (
                <span key={i} className="flex items-center gap-1 text-xs text-zinc-500">
                  <span className={cn('w-2 h-2 rounded-full', seg.color)} />
                  {seg.label} — {seg.pct.toFixed(0)}%
                </span>
              ))}
            </div>
            <span
              className={cn(
                'text-xs font-semibold tabular-nums shrink-0',
                totalIsValid ? 'text-emerald-600' : 'text-amber-600',
              )}
            >
              Total: {totalWeight}%
              {!totalIsValid && totalWeight > 0 && (
                <span className="ml-1 font-normal">
                  ({totalWeight < 100 ? `${100 - totalWeight}% unallocated` : `${totalWeight - 100}% over`})
                </span>
              )}
            </span>
          </div>
        </div>

        {/* Members list */}
        <div className="space-y-3 pt-1">
          {members.length === 0 && (
            <p className="text-sm text-zinc-400 text-center py-4 border border-dashed border-zinc-200 rounded-lg">
              No members — add one below
            </p>
          )}

          {members.map((member, i) => {
            const color = getMemberColor(i);
            return (
              <div
                key={member.assignee_id}
                className="border border-zinc-200 rounded-lg p-4 space-y-3 bg-zinc-50/50"
              >
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className={cn(
                        'w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0',
                        color.bar,
                      )}
                    >
                      {member.display_name.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-sm font-medium text-zinc-800">
                      {member.display_name}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs text-zinc-500 hover:text-zinc-700"
                      onClick={() =>
                        updateMember(i, { weight: member.weight === 0 ? 50 : 0 })
                      }
                    >
                      {member.weight === 0 ? 'Restore' : 'Pause'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-zinc-400 hover:text-red-500"
                      onClick={() => removeMember(i)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>

                {/* Percentage input */}
                <div className="flex items-center gap-3">
                  <div className="space-y-1">
                    <Label htmlFor={`weight-${i}`} className="text-xs text-zinc-500">
                      % of assignments
                    </Label>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <Input
                          id={`weight-${i}`}
                          type="number"
                          min={0}
                          max={100}
                          value={member.weight}
                          onChange={(e) =>
                            updateMember(i, {
                              weight: Math.min(
                                100,
                                Math.max(0, parseInt(e.target.value, 10) || 0),
                              ),
                            })
                          }
                          className="h-8 w-20 text-sm pr-6"
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-zinc-400 pointer-events-none">
                          %
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Availability */}
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() =>
                      updateMember(i, {
                        has_availability: !member.has_availability,
                        available_from: '',
                        available_until: '',
                      })
                    }
                    className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-700 transition-colors"
                  >
                    <CalendarIcon className="h-3.5 w-3.5" />
                    {member.has_availability ? (
                      <>
                        <X className="h-3 w-3" />
                        Clear availability window
                      </>
                    ) : (
                      '+ Set availability window'
                    )}
                  </button>

                  {member.has_availability && (
                    <div className="flex items-center gap-4 pl-5">
                      <div className="space-y-1">
                        <Label className="text-xs text-zinc-400">Available from</Label>
                        <Input
                          type="date"
                          value={member.available_from}
                          onChange={(e) =>
                            updateMember(i, { available_from: e.target.value })
                          }
                          className="h-8 text-xs"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-zinc-400">Available until</Label>
                        <Input
                          type="date"
                          value={member.available_until}
                          onChange={(e) =>
                            updateMember(i, { available_until: e.target.value })
                          }
                          className="h-8 text-xs"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Add member */}
        {availableToAdd.length > 0 && (
          <div className="flex items-center gap-2 pt-1">
            <Select value={addingUserId} onValueChange={setAddingUserId}>
              <SelectTrigger className="h-9 flex-1 text-sm">
                <SelectValue placeholder="Add a team member…" />
              </SelectTrigger>
              <SelectContent>
                {availableToAdd.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {getUserDisplayName(u)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              className="h-9 gap-1.5"
              onClick={addMember}
              disabled={!addingUserId}
            >
              <Plus className="h-3.5 w-3.5" />
              Add
            </Button>
          </div>
        )}

        <DialogFooter className="pt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? 'Saving…' : 'Save Distribution'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
